import { Link } from "react-router-dom";
import type { Interaction } from "../../types";
import { isCruiseDay, isCruiseSegment, isTravelSegment } from "../../types";
import type { SegmentWithId } from "../../hooks/useSegments";
import type { EntityInfo } from "../../hooks/useEntityMap";
import {
  segmentDayLookup, segmentZoneLookup, formatSegmentDateRange, isSegmentCurrent, orderChildSegments, segmentDayCount, formatDuration } from "../../utils/tripDisplay";
import { dayTypeIcon, dayTypeLabel } from "../../utils/cruiseItinerary";
import { ActivityList } from "./ActivityList";
import { NotesDisplay } from "../common/NotesDisplay";
import { dayKeyOf, formatDayKey } from "../../utils/calendarDay";
import { travelDaySummary } from "../../utils/travelDay";

interface SegmentBlockProps {
  segment: SegmentWithId;
  allSegments: SegmentWithId[];
  /** The trip's zone; segments below it may override for their own days. */
  tripZone?: string | null;
  interactions: (Interaction & { id: string })[];
  entityMap: Record<string, string>;
  entityDataMap: Record<string, EntityInfo>;
  tripId: string;
  expandedSegments: Set<string>;
  toggleSegment: (id: string) => void;
  /** When provided, day/segment cards render a "+ Add activity" button. */
  onAddActivity?: (segmentId: string) => void;
  /** "asc" = oldest → newest (default), "desc" = newest first. */
  sortDir?: "asc" | "desc";
  /** segmentId → most recent activity time (ms) in its subtree; used for desc order. */
  activityMs?: Map<string, number>;
  /**
   * The segment you're in right now, plus its ancestors — see
   * `activeSegmentChainIds`. Only these read as "NOW": on a day you move
   * between two segments both cover today, and tagging the one you've left
   * is what makes activities look like they belong there. Omit to fall back
   * to plain "covers today".
   */
  activeSegmentIds?: Set<string>;
}

export function SegmentBlock({
  segment,
  allSegments,
  tripZone,
  interactions,
  entityMap,
  entityDataMap,
  tripId,
  expandedSegments,
  toggleSegment,
  onAddActivity,
  sortDir = "asc",
  activityMs,
  activeSegmentIds,
}: SegmentBlockProps) {
  const isExpanded = expandedSegments.has(segment.id);
  // Days for activities that carry no times of their own.
  const onDay = segmentDayLookup(allSegments);
  const onZone = segmentZoneLookup(allSegments, tripZone ?? undefined);
  // allSegments is startDate-asc (calendar order = the "oldest" view). For
  // "newest" order children by their most recent activity so the active one
  // rises to the top — except a cruise's numbered days, which always read
  // Day 1 → Day N.
  const childSegments = orderChildSegments(
    allSegments.filter((s) => s.parentSegmentId === segment.id),
    sortDir,
    activityMs,
  );
  const segActivities = interactions.filter((i) => i.segmentId === segment.id);
  const days = segmentDayCount(segment.startDate, segment.endDate);
  const isDay = isCruiseDay(segment);
  const isCruise = !isDay && isCruiseSegment(segment);
  const isTravel = !isDay && isTravelSegment(segment);
  const travel = isTravel ? travelDaySummary(segActivities, (id) => entityDataMap[id]) : null;

  const countActivities = (segId: string): number => {
    const direct = interactions.filter((i) => i.segmentId === segId).length;
    const children = allSegments.filter((s) => s.parentSegmentId === segId);
    return direct + children.reduce((sum, c) => sum + countActivities(c.id), 0);
  };
  const totalActivities = countActivities(segment.id);
  const entityImage = segment.entityId ? entityDataMap[segment.entityId]?.imageUrl : null;
  const isCurrent = activeSegmentIds
    ? activeSegmentIds.has(segment.id)
    : isSegmentCurrent(segment.startDate, segment.endDate);

  const dayKey = dayKeyOf(segment.startDate);
  const dayDateLabel = dayKey
    ? formatDayKey(dayKey, { weekday: "short", month: "short", day: "numeric" })
    : "";

  const cfg = segment.cruiseConfig;

  return (
    <div className={`segment-block ${isExpanded ? "segment-block-expanded" : ""} ${isCurrent ? "segment-block-current" : ""} ${isDay ? "segment-block-day" : ""} ${isCruise ? "segment-block-cruise" : ""}`}>
      <button className="segment-block-header" onClick={() => toggleSegment(segment.id)}>
        {isDay ? (
          <span className="segment-day-icon" aria-hidden="true">{dayTypeIcon(segment.dayType)}</span>
        ) : entityImage ? (
          <img src={entityImage} alt="" className="segment-block-thumb" />
        ) : isCruise ? (
          <span className="segment-day-icon" aria-hidden="true">🚢</span>
        ) : isTravel ? (
          <span className="segment-day-icon" aria-hidden="true">🧳</span>
        ) : null}
        <div className="segment-block-info">
          <h3>
            {isDay && typeof segment.dayNumber === "number" && (
              <span className="segment-day-num">Day {segment.dayNumber}</span>
            )}
            {isDay && segment.portName?.trim()
              ? segment.portName.trim()
              : isDay
                ? dayTypeLabel(segment.dayType)
                : segment.name}
            {isCurrent && <span className="segment-now-pill">NOW</span>}
          </h3>
          <div className="segment-block-meta">
            {isDay ? (
              <>
                {dayDateLabel && <span>{dayDateLabel}</span>}
                {segment.dayType && (
                  <>
                    <span className="meta-sep">·</span>
                    <span>{dayTypeLabel(segment.dayType)}</span>
                  </>
                )}
              </>
            ) : (
              <>
                <span>{formatSegmentDateRange(segment.startDate, segment.endDate)}</span>
                <span className="meta-sep">·</span>
                <span>{isCruise && cfg?.nights != null ? `${cfg.nights} night${cfg.nights === 1 ? "" : "s"}` : `${days} day${days !== 1 ? "s" : ""}`}</span>
                {segment.entityId && entityMap[segment.entityId] && (
                  <>
                    <span className="meta-sep">·</span>
                    <span className="segment-block-entity">{entityMap[segment.entityId]}</span>
                  </>
                )}
                {travel?.route && (
                  <>
                    <span className="meta-sep">·</span>
                    <span className="segment-travel-route">{travel.route}</span>
                  </>
                )}
                {travel?.doorToDoorMs != null && (
                  <>
                    <span className="meta-sep">·</span>
                    <span title="First logged step to last">{formatDuration(travel.doorToDoorMs / 1000)} door to door</span>
                  </>
                )}
                {isCruise && cfg?.homePort && (
                  <>
                    <span className="meta-sep">·</span>
                    <span>from {cfg.homePort}</span>
                  </>
                )}
              </>
            )}
            <span className="meta-sep">·</span>
            <span>{totalActivities} activit{totalActivities !== 1 ? "ies" : "y"}</span>
          </div>
        </div>
      </button>

      {isExpanded && (
        <div className="segment-block-body">
          {segment.notes && <NotesDisplay value={segment.notes} className="segment-block-notes" />}

          {childSegments.length > 0 && (
            <div className="segment-block-children">
              {childSegments.map((child) => (
                <SegmentBlock
                  key={child.id}
                  segment={child}
                  allSegments={allSegments}
                  tripZone={tripZone}
                  interactions={interactions}
                  entityMap={entityMap}
                  entityDataMap={entityDataMap}
                  tripId={tripId}
                  expandedSegments={expandedSegments}
                  toggleSegment={toggleSegment}
                  onAddActivity={onAddActivity}
                  sortDir={sortDir}
                  activityMs={activityMs}
                  activeSegmentIds={activeSegmentIds}
                />
              ))}
            </div>
          )}

          {segActivities.length > 0 && (
            <ActivityList
              activities={segActivities}
              entityMap={entityMap}
              entityDataMap={entityDataMap}
              tripId={tripId}
              sortDir={sortDir}
              onDay={onDay}
              onZone={onZone}
            />
          )}

          {childSegments.length === 0 && segActivities.length === 0 && (
            <p className="text-muted">{isCruise ? "No itinerary days yet — open the cruise to generate them." : "No activities yet."}</p>
          )}

          <div className="segment-block-body-actions">
            {onAddActivity && !isCruise && (
              <button
                type="button"
                className="btn btn-small btn-secondary"
                onClick={() => onAddActivity(segment.id)}
              >
                + Add activity
              </button>
            )}
            <Link to={`/trips/${tripId}/segments/${segment.id}`} className="segment-detail-link">
              {isDay ? "Edit day" : isCruise ? "Open cruise" : "Segment details"} &rarr;
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
